import { useState } from 'react'
import type { KeyboardEvent, ReactNode } from 'react'
import {
  Button,
  Checkbox,
  CheckboxGroup,
  ComboBox,
  Description,
  FieldError,
  Header,
  Input,
  InputGroup,
  Label,
  ListBox,
  ListBoxItem,
  NumberField,
  Select,
  Switch,
  TextArea,
  TextField,
} from '@heroui/react'
import { Controller } from 'react-hook-form'
import type { Control, FieldPath, FieldValues } from 'react-hook-form'

import { AlertIcon, CloseIcon, EyeIcon, EyeOffIcon } from './icons'

interface BaseFieldProps<TValues extends FieldValues> {
  control: Control<TValues>
  name: FieldPath<TValues>
  label: string
  /**
   * Takes the label out of the layout but leaves it in the accessibility tree,
   * for the places something else already names the field — a grid's column
   * heading, or the title of the card the field is alone inside. Never a way to
   * drop the label: a field with no accessible name is a field a screen reader
   * cannot announce.
   */
  isLabelHidden?: boolean
  description?: ReactNode
  placeholder?: string
  isRequired?: boolean
  isDisabled?: boolean
  autoComplete?: string
  autoFocus?: boolean
}

interface TextFieldControlProps<TValues extends FieldValues> extends BaseFieldProps<TValues> {
  type?: 'text' | 'email' | 'password' | 'tel' | 'url'
}

/**
 * React Aria drives TextField by value, not by DOM events, so react-hook-form is
 * wired in through a Controller rather than `register`.
 */
export function TextFieldControl<TValues extends FieldValues>({
  control,
  name,
  label,
  description,
  placeholder,
  isRequired,
  isDisabled,
  autoComplete,
  autoFocus,
  isLabelHidden,
  type = 'text',
}: TextFieldControlProps<TValues>) {
  return (
    <Controller
      control={control}
      name={name}
      render={({ field, fieldState }) => (
        <TextField
          name={field.name}
          value={field.value ?? ''}
          onChange={field.onChange}
          onBlur={field.onBlur}
          isInvalid={fieldState.invalid}
          isRequired={isRequired}
          isDisabled={isDisabled}
          fullWidth
          className="flex flex-col gap-1.5"
        >
          <Label className={isLabelHidden ? 'sr-only' : undefined}>{label}</Label>
          <Input
            ref={field.ref}
            type={type}
            placeholder={placeholder}
            autoComplete={autoComplete}
            autoFocus={autoFocus}
          />
          {description && <Description>{description}</Description>}
          <FieldError>{fieldState.error?.message}</FieldError>
        </TextField>
      )}
    />
  )
}

/** Same contract as `TextFieldControl`, with a show/hide toggle in the suffix. */
export function PasswordFieldControl<TValues extends FieldValues>({
  control,
  name,
  label,
  description,
  placeholder,
  isRequired,
  isDisabled,
  autoComplete = 'current-password',
  autoFocus,
}: BaseFieldProps<TValues>) {
  const [isVisible, setIsVisible] = useState(false)
  const [isCapsLockOn, setIsCapsLockOn] = useState(false)

  // Caps Lock is the single most common cause of a "wrong password" that is not
  // wrong; the browser only reports it while a key is down, so both edges are read.
  const trackCapsLock = (event: KeyboardEvent<HTMLInputElement>) => {
    setIsCapsLockOn(event.getModifierState('CapsLock'))
  }

  return (
    <Controller
      control={control}
      name={name}
      render={({ field, fieldState }) => (
        <TextField
          name={field.name}
          value={field.value ?? ''}
          onChange={field.onChange}
          onBlur={field.onBlur}
          isInvalid={fieldState.invalid}
          isRequired={isRequired}
          isDisabled={isDisabled}
          fullWidth
          className="flex flex-col gap-1.5"
        >
          <Label>{label}</Label>
          <InputGroup>
            <InputGroup.Input
              ref={field.ref}
              type={isVisible ? 'text' : 'password'}
              placeholder={placeholder}
              autoComplete={autoComplete}
              autoFocus={autoFocus}
              onKeyDown={trackCapsLock}
              onKeyUp={trackCapsLock}
              onBlur={() => setIsCapsLockOn(false)}
            />
            <InputGroup.Suffix>
              <Button
                variant="ghost"
                isIconOnly
                excludeFromTabOrder
                aria-label={isVisible ? 'Hide password' : 'Show password'}
                onPress={() => setIsVisible((visible) => !visible)}
                className="text-muted hover:text-foreground size-10 transition-colors duration-200"
              >
                {isVisible ? <EyeOffIcon /> : <EyeIcon />}
              </Button>
            </InputGroup.Suffix>
          </InputGroup>
          {isCapsLockOn && (
            <p className="flex items-center gap-1.5 text-xs text-amber-700" aria-live="polite">
              <AlertIcon width={13} height={13} />
              Caps Lock is on
            </p>
          )}
          {description && <Description>{description}</Description>}
          <FieldError>{fieldState.error?.message}</FieldError>
        </TextField>
      )}
    />
  )
}

interface TextAreaFieldControlProps<TValues extends FieldValues> extends BaseFieldProps<TValues> {
  rows?: number
  /** Renders the value in the mono stack — for keys, hashes and other opaque strings. */
  isMonospaced?: boolean
}

/** `TextFieldControl` for multi-line values; same Controller wiring, textarea input. */
export function TextAreaFieldControl<TValues extends FieldValues>({
  control,
  name,
  label,
  description,
  placeholder,
  isRequired,
  isDisabled,
  autoFocus,
  rows = 4,
  isMonospaced = false,
}: TextAreaFieldControlProps<TValues>) {
  return (
    <Controller
      control={control}
      name={name}
      render={({ field, fieldState }) => (
        <TextField
          name={field.name}
          value={field.value ?? ''}
          onChange={field.onChange}
          onBlur={field.onBlur}
          isInvalid={fieldState.invalid}
          isRequired={isRequired}
          isDisabled={isDisabled}
          fullWidth
          className="flex flex-col gap-1.5"
        >
          <Label>{label}</Label>
          <TextArea
            ref={field.ref}
            rows={rows}
            placeholder={placeholder}
            autoFocus={autoFocus}
            // Key material is not prose: autocorrect and capitalisation would
            // silently corrupt a pasted value.
            spellCheck={false}
            autoCapitalize="off"
            autoCorrect="off"
            className={isMonospaced ? 'resize-y font-mono text-[11px] leading-relaxed' : 'resize-y'}
          />
          {description && <Description>{description}</Description>}
          <FieldError>{fieldState.error?.message}</FieldError>
        </TextField>
      )}
    />
  )
}

/* -------------------------------------------------------------------------- */
/* Choice, number and toggle                                                   */
/* -------------------------------------------------------------------------- */

export interface SelectOption {
  value: string
  label: string
}

/** A titled run of options in a select's list — "Weight", then its units. */
export interface SelectOptionGroup {
  label: string
  options: SelectOption[]
}

type SelectFieldControlProps<TValues extends FieldValues> = BaseFieldProps<TValues> & {
  /** Rendered as the trigger's text while nothing is chosen. */
  placeholder?: string
} & (
    | { options: SelectOption[]; groups?: never }
    /** The same choice, listed under headings — for a list long enough to want them. */
    | { groups: SelectOptionGroup[]; options?: never }
  )

/**
 * Single choice from a short, fixed list. React Aria keys its selection by id, so the
 * option's `value` is the key and an unset field reads as `null` rather than `''` —
 * an empty string is a key the list does not contain, which clears the trigger's text.
 */
export function SelectFieldControl<TValues extends FieldValues>({
  control,
  name,
  label,
  description,
  placeholder = 'Select…',
  options,
  groups,
  isRequired,
  isDisabled,
}: SelectFieldControlProps<TValues>) {
  const renderOption = (option: SelectOption) => (
    <ListBoxItem key={option.value} id={option.value} textValue={option.label}>
      {option.label}
    </ListBoxItem>
  )

  return (
    <Controller
      control={control}
      name={name}
      render={({ field, fieldState }) => (
        <Select
          selectedKey={field.value ?? null}
          onSelectionChange={(key) => field.onChange(key === null ? undefined : String(key))}
          onBlur={field.onBlur}
          isInvalid={fieldState.invalid}
          isRequired={isRequired}
          isDisabled={isDisabled}
          fullWidth
          className="flex flex-col gap-1.5"
        >
          <Label>{label}</Label>
          <Select.Trigger ref={field.ref}>
            <Select.Value>
              {/* An unset Select reports an empty string, not undefined, so `??` would
                  never reach the placeholder. */}
              {({ selectedText }) =>
                selectedText || <span className="text-muted">{placeholder}</span>
              }
            </Select.Value>
            <Select.Indicator />
          </Select.Trigger>
          <Select.Popover>
            <ListBox>
              {groups
                ? groups.map((group) => (
                    <ListBox.Section key={group.label} id={group.label}>
                      <Header>{group.label}</Header>
                      {group.options.map(renderOption)}
                    </ListBox.Section>
                  ))
                : options.map(renderOption)}
            </ListBox>
          </Select.Popover>
          {description && <Description>{description}</Description>}
          <FieldError>{fieldState.error?.message}</FieldError>
        </Select>
      )}
    />
  )
}

interface MultiSelectFieldControlProps<
  TValues extends FieldValues,
> extends BaseFieldProps<TValues> {
  options: SelectOption[]
  /** Rendered as the trigger's text while nothing is chosen. */
  placeholder?: string
}

/**
 * Several choices from a dropdown, held as an array of the options' `value`s.
 *
 * The trigger summarises rather than listing every pick — a branch name per pick
 * would grow it past the column it sits in. The popover stays open while rows are
 * ticked, so choosing three outlets is one trip rather than three.
 */
export function MultiSelectFieldControl<TValues extends FieldValues>({
  control,
  name,
  label,
  description,
  placeholder = 'Select…',
  options,
  isRequired,
  isDisabled,
  isLabelHidden,
}: MultiSelectFieldControlProps<TValues>) {
  return (
    <Controller
      control={control}
      name={name}
      render={({ field, fieldState }) => {
        const value: string[] = field.value ?? []
        const summary =
          value.length === 0
            ? null
            : value.length === 1
              ? (options.find((option) => option.value === value[0])?.label ?? '1 selected')
              : `${value.length} selected`

        return (
          <Select
            selectionMode="multiple"
            value={value}
            onChange={(keys) => field.onChange(keys.map(String))}
            onBlur={field.onBlur}
            isInvalid={fieldState.invalid}
            isRequired={isRequired}
            isDisabled={isDisabled}
            fullWidth
            className="flex flex-col gap-1.5"
          >
            <Label className={isLabelHidden ? 'sr-only' : undefined}>{label}</Label>
            <Select.Trigger ref={field.ref}>
              <span className="flex-1 truncate text-start">
                {summary ?? <span className="text-muted">{placeholder}</span>}
              </span>
              <Select.Indicator />
            </Select.Trigger>
            <Select.Popover>
              <ListBox>
                {options.map((option) => (
                  <ListBoxItem key={option.value} id={option.value} textValue={option.label}>
                    <span className="flex-1 truncate">{option.label}</span>
                    <ListBox.ItemIndicator />
                  </ListBoxItem>
                ))}
              </ListBox>
            </Select.Popover>
            {description && <Description>{description}</Description>}
            <FieldError>{fieldState.error?.message}</FieldError>
          </Select>
        )
      }}
    />
  )
}

interface ComboBoxFieldControlProps<TValues extends FieldValues> extends BaseFieldProps<TValues> {
  options: SelectOption[]
  /** Shown in the box while nothing is chosen and nothing is typed. */
  placeholder?: string
  /** Shown in the popover when the typing matches nothing. */
  emptyMessage?: string
}

/**
 * One choice from a list too long to scroll — typed into to narrow it — that can
 * also be left empty. Held as the option's `value`, or `null` for none.
 *
 * Emptied two ways: deleting the text, which React Aria reads as clearing the
 * selection, and the × beside the arrow, since the first is not something a
 * merchant would think to try. The × is a plain `<button>` on purpose — a React
 * Aria one inside a combo box picks up the trigger's context and opens the list
 * instead of clearing it.
 */
export function ComboBoxFieldControl<TValues extends FieldValues>({
  control,
  name,
  label,
  description,
  placeholder = 'Search…',
  emptyMessage = 'Nothing matches.',
  options,
  isRequired,
  isDisabled,
  isLabelHidden,
}: ComboBoxFieldControlProps<TValues>) {
  return (
    <Controller
      control={control}
      name={name}
      render={({ field, fieldState }) => {
        const value: string | null = field.value ?? null
        const isClearable = Boolean(value) && !isDisabled

        return (
          <ComboBox
            value={value}
            onChange={(key) => field.onChange(key === null ? null : String(key))}
            onBlur={field.onBlur}
            isInvalid={fieldState.invalid}
            isRequired={isRequired}
            isDisabled={isDisabled}
            fullWidth
            className="flex flex-col gap-1.5"
          >
            <Label className={isLabelHidden ? 'sr-only' : undefined}>{label}</Label>
            {/* The theme pins the arrow over the input's end and pads the input
                for it only when the two are adjacent. The × sits between them,
                so it is pinned the same way, just inside the arrow, and the
                input is padded for both itself. */}
            <ComboBox.InputGroup>
              <Input
                ref={field.ref}
                placeholder={placeholder}
                className={isClearable ? 'pe-14' : undefined}
              />
              {isClearable && (
                <button
                  type="button"
                  aria-label={`Clear ${label.toLowerCase()}`}
                  onClick={() => field.onChange(null)}
                  className="text-muted hover:text-foreground absolute end-7 top-1/2 z-10 flex size-6 -translate-y-1/2 items-center justify-center rounded-full transition-colors"
                >
                  <CloseIcon width={14} height={14} />
                </button>
              )}
              <ComboBox.Trigger />
            </ComboBox.InputGroup>
            <ComboBox.Popover>
              <ListBox
                renderEmptyState={() => (
                  <p className="text-muted px-3 py-2 text-xs">{emptyMessage}</p>
                )}
              >
                {options.map((option) => (
                  <ListBoxItem key={option.value} id={option.value} textValue={option.label}>
                    <span className="flex-1 truncate">{option.label}</span>
                    <ListBox.ItemIndicator />
                  </ListBoxItem>
                ))}
              </ListBox>
            </ComboBox.Popover>
            {description && <Description>{description}</Description>}
            <FieldError>{fieldState.error?.message}</FieldError>
          </ComboBox>
        )
      }}
    />
  )
}

interface NumberFieldControlProps<TValues extends FieldValues> extends BaseFieldProps<TValues> {
  minValue?: number
  maxValue?: number
  step?: number
  /** Formats the value as currency in the field itself. */
  formatOptions?: Intl.NumberFormatOptions
  /**
   * Drops the steppers and right-aligns the figure, for a number in a data grid:
   * two buttons either side cost more width than a column has, the arrow keys
   * still step the value, and prices that line up on the decimal are the point
   * of putting them in a column at all.
   */
  isCompact?: boolean
}

/**
 * A number, kept a number. React Aria reports an emptied field as `NaN`, which would
 * satisfy a `z.number()` check while being unusable — it is passed on as `undefined`
 * so the schema reports the field as missing instead.
 */
export function NumberFieldControl<TValues extends FieldValues>({
  control,
  name,
  label,
  description,
  placeholder,
  minValue,
  maxValue,
  step,
  formatOptions,
  isRequired,
  isDisabled,
  isLabelHidden,
  isCompact,
}: NumberFieldControlProps<TValues>) {
  return (
    <Controller
      control={control}
      name={name}
      render={({ field, fieldState }) => (
        <NumberField
          value={field.value ?? Number.NaN}
          onChange={(value) => field.onChange(Number.isNaN(value) ? undefined : value)}
          onBlur={field.onBlur}
          minValue={minValue}
          maxValue={maxValue}
          step={step}
          formatOptions={formatOptions}
          isInvalid={fieldState.invalid}
          isRequired={isRequired}
          isDisabled={isDisabled}
          fullWidth
          className="flex flex-col gap-1.5"
        >
          <Label className={isLabelHidden ? 'sr-only' : undefined}>{label}</Label>
          {/* HeroUI lays the group out as a `40px 1fr 40px` grid for the −/+.
              Without them the input would sit in the first 40px track, so a
              compact field is one track the width of the field. */}
          <NumberField.Group className={isCompact ? 'w-full grid-cols-1' : 'w-full'}>
            {!isCompact && <NumberField.DecrementButton className="shrink-0" />}
            <NumberField.Input
              ref={field.ref}
              placeholder={placeholder}
              // A cell in a grid is overwritten far more often than it is edited, so
              // landing in it selects the figure: tab, type, tab. Without this the
              // old value has to be cleared by hand before the new one can go in.
              onFocus={isCompact ? (event) => event.currentTarget.select() : undefined}
              inputMode="decimal"
              className={
                isCompact ? 'min-w-0 flex-1 text-right tabular-nums' : 'min-w-0 flex-1 text-center'
              }
            />
            {!isCompact && <NumberField.IncrementButton className="shrink-0" />}
          </NumberField.Group>
          {description && <Description>{description}</Description>}
          <FieldError>{fieldState.error?.message}</FieldError>
        </NumberField>
      )}
    />
  )
}

/** A single on/off value. The label sits inside the switch so the whole row toggles. */
export function SwitchFieldControl<TValues extends FieldValues>({
  control,
  name,
  label,
  description,
  isDisabled,
}: BaseFieldProps<TValues>) {
  return (
    <Controller
      control={control}
      name={name}
      render={({ field }) => (
        <Switch
          isSelected={Boolean(field.value)}
          onChange={field.onChange}
          isDisabled={isDisabled}
          className="flex flex-col gap-1.5"
        >
          <Switch.Content>
            <Switch.Control>
              <Switch.Thumb />
            </Switch.Control>
            <Label>{label}</Label>
          </Switch.Content>
          {description && <Description>{description}</Description>}
        </Switch>
      )}
    />
  )
}

interface CheckboxGroupFieldControlProps<
  TValues extends FieldValues,
> extends BaseFieldProps<TValues> {
  options: SelectOption[]
  /** Shown in place of the list when there is nothing to choose from. */
  emptyMessage?: string
}

/**
 * Many choices from a list that can run long — categories, mostly.
 *
 * A checkbox list rather than a multi-select popover: the values are short, the
 * merchant usually picks one or two, and everything stays visible while they do.
 */
export function CheckboxGroupFieldControl<TValues extends FieldValues>({
  control,
  name,
  label,
  description,
  options,
  emptyMessage = 'Nothing to choose from yet.',
  isDisabled,
  isLabelHidden,
}: CheckboxGroupFieldControlProps<TValues>) {
  return (
    <Controller
      control={control}
      name={name}
      render={({ field, fieldState }) => (
        <CheckboxGroup
          value={field.value ?? []}
          onChange={field.onChange}
          isInvalid={fieldState.invalid}
          isDisabled={isDisabled}
          className="flex flex-col gap-1.5"
        >
          <Label className={isLabelHidden ? 'sr-only' : undefined}>{label}</Label>

          {options.length === 0 ? (
            <p className="text-muted border-border rounded-xl border border-dashed px-3 py-4 text-xs">
              {emptyMessage}
            </p>
          ) : (
            <div className="border-border max-h-48 overflow-y-auto rounded-xl border p-3">
              <div className="flex flex-col gap-2">
                {options.map((option) => (
                  <Checkbox key={option.value} value={option.value}>
                    <Checkbox.Content>
                      <Checkbox.Control>
                        <Checkbox.Indicator />
                      </Checkbox.Control>
                      <Label>{option.label}</Label>
                    </Checkbox.Content>
                  </Checkbox>
                ))}
              </div>
            </div>
          )}

          {description && <Description>{description}</Description>}
          <FieldError>{fieldState.error?.message}</FieldError>
        </CheckboxGroup>
      )}
    />
  )
}

/**
 * A calendar day, wired into the form.
 *
 * A native `<input type="date">` rather than HeroUI's DatePicker: the values
 * this app collects are plain days — a campaign window, a report range — and the
 * native control is already localised, keyboard-friendly and free. The value is
 * the `YYYY-MM-DD` string the input speaks, so callers convert to and from ISO
 * at the edges rather than mid-form.
 */
export function DateFieldControl<TValues extends FieldValues>({
  control,
  name,
  label,
  description,
  min,
  max,
  isRequired,
  isDisabled,
}: BaseFieldProps<TValues> & { min?: string; max?: string }) {
  return (
    <Controller
      control={control}
      name={name}
      render={({ field, fieldState }) => (
        <div className="flex flex-col gap-1.5">
          <Label>{label}</Label>
          <input
            ref={field.ref}
            type="date"
            value={field.value ?? ''}
            onChange={(event) => field.onChange(event.target.value)}
            onBlur={field.onBlur}
            min={min}
            max={max}
            required={isRequired}
            disabled={isDisabled}
            aria-invalid={fieldState.invalid || undefined}
            className="border-border bg-background aria-[invalid]:border-danger h-10 rounded-xl border px-3 text-sm"
          />
          {description && <Description>{description}</Description>}
          {fieldState.error?.message && (
            <p role="alert" className="text-danger text-xs">
              {fieldState.error.message}
            </p>
          )}
        </div>
      )}
    />
  )
}
